// frontend/src/pages/FrontdeskDashboard.jsx

import React, { useState, useMemo,useEffect } from "react";
import "../styles/frontdesk.css";
import { API_BASE_URL } from "../services/apiConfig";
import { useRestaurant } from "../contexts/RestaurantContext";

const getUsernameFromToken = () => {
  const token = sessionStorage.getItem("casapizza_token");
  if (!token) return "cajero";
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.sub || "cajero";
  } catch {
    return "cajero";
  }
};

function FrontdeskDashboard() {
  const { restaurant } = useRestaurant();
  const branches = useMemo(() => restaurant?.branches || [], [restaurant]);

  const [ticket, setTicket] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("EFECTIVO");
  const [notes, setNotes] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [sending, setSending] = useState(false);
  const [lastOrderId, setLastOrderId] = useState(null);
  const [showPizzaModal, setShowPizzaModal] = useState(false);
  const [selectedPizza, setSelectedPizza] = useState(null);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);

  const [showShiftModal, setShowShiftModal] = useState(false);
  const [shiftView, setShiftView] = useState("summary"); // "summary" | "history"
  const [shiftSummary, setShiftSummary] = useState(null);
  const [cutHistory, setCutHistory] = useState([]);
  const [closingShift, setClosingShift] = useState(false);

  const [catalog, setCatalog] = useState({
  pizza_sizes: [],
  drinks: [],
  packages: []
});

  useEffect(() => {
    if (selectedBranch === "" && branches.length > 0) {
      setSelectedBranch(branches[0].id);
    }
  }, [branches, selectedBranch]);

  useEffect(() => {
    loadCatalog(selectedBranch);
  }, [selectedBranch]);

  const loadCatalog = async (branchId) => {
  try {
    const token = sessionStorage.getItem('casapizza_token');
    const url = branchId
      ? `${API_BASE_URL}/frontdesk/catalog?branch_id=${branchId}`
      : `${API_BASE_URL}/frontdesk/catalog`;
    const response = await fetch(
      url,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );

    const data = await response.json();

    setCatalog(data);

  } catch (error) {
    console.error("Error cargando catálogo", error);
  }
};

  const addProduct = (product) => {
    const exists = ticket.find((item) => item.id === product.id);
    if (exists) {
      setTicket(
        ticket.map((item) =>
          item.id === product.id
            ? { ...item, qty: item.qty + 1 }
            : item
        )
      );
    } else {
      setTicket([...ticket, { ...product, qty: 1 }]);
    }
  };

  const increaseQty = (id) => {
    setTicket(
      ticket.map((item) =>
        item.id === id ? { ...item, qty: item.qty + 1 } : item
      )
    );
  };

  const decreaseQty = (id) => {
    setTicket(
      ticket
        .map((item) =>
          item.id === id ? { ...item, qty: item.qty - 1 } : item
        )
        .filter((item) => item.qty > 0)
    );
  };

  const removeItem = (id) => {
    setTicket(ticket.filter((item) => item.id !== id));
  };

  const subtotal = useMemo(() => {
    return ticket.reduce(
      (sum, item) => sum + item.price * item.qty,
      0
    );
  }, [ticket]);

  const total = subtotal;

  const clearOrder = () => {
    setTicket([]);
    setCustomerName("");
    setCustomerPhone("");
    setNotes("");
    setPaymentMethod("EFECTIVO");
  };

  const loadShiftSummary = async () => {
    const token = sessionStorage.getItem("casapizza_token");
    const res = await fetch(
      `${API_BASE_URL}/frontdesk/shift/summary?branch_id=${selectedBranch}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (res.ok) setShiftSummary(await res.json());
  };

  const loadCutHistory = async () => {
    const token = sessionStorage.getItem("casapizza_token");
    const res = await fetch(
      `${API_BASE_URL}/frontdesk/cuts?branch_id=${selectedBranch}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (res.ok) setCutHistory(await res.json());
  };

  const openShiftModal = async () => {
    setShiftView("summary");
    setShowShiftModal(true);
    await loadShiftSummary();
  };

  const executeCloseShift = async () => {
    if (!window.confirm("¿Ejecutar corte de caja? Esta acción cerrará el turno actual.")) return;
    setClosingShift(true);
    try {
      const token = sessionStorage.getItem("casapizza_token");
      const res = await fetch(`${API_BASE_URL}/frontdesk/shift/close`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          branch_id: selectedBranch,
          closed_by: getUsernameFromToken(),
        }),
      });
      if (res.ok) {
        await loadCutHistory();
        setShiftView("history");
        await loadShiftSummary();
      }
    } finally {
      setClosingShift(false);
    }
  };

  const submitOrder = async () => {
    if (ticket.length === 0) {
      alert("Agrega productos al ticket.");
      return;
    }

    try {
      setSending(true);

      // ⚠️ Ajusta endpoint real cuando conectes backend
      const payload = {
         customer_name: customerName || "Mostrador",
         customer_phone: customerPhone || "N/A",
         branch_id: selectedBranch,
         total_amount: total,
         payment_method: paymentMethod,
         notes: notes,
         //ticket_items: ticket
         items: ticket.map((item) => ({
            product_name: item.name,
            quantity: item.qty,
            price: item.price,
            specialties: item.specialties||[]
         }))
      };

      const token = sessionStorage.getItem('casapizza_token');
      const response = await fetch(
        `${API_BASE_URL}/frontdesk/order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("No se pudo crear la orden");
      }

      const data = await response.json();

      setLastOrderId(data.order_id);
      clearOrder();
    } catch (error) {
      console.error(error);
      alert("Error al enviar la orden.");
    } finally {
      setSending(false);
    }
  };

  

  const categories = [
  "PIZZAS",
  "BEBIDAS",
  "COMBOS"
   ];

   const getProductsByCategory = (category) => {
        switch (category) {
           case "PIZZAS":
             return (catalog.pizza_sizes || []).map((item) => ({
               id: "pizza-" + item.id,
               name: "Pizza " + item.name,
               price: item.price,
               max_specialties: item.max_specialties
             }));

           case "BEBIDAS":
             return (catalog.drinks || []).map((item) => ({
               id: "drink-" + item.id,
               name: item.name,
               price: item.price
             }));

           case "COMBOS":
             return (catalog.packages || []).map((item) => ({
               id: "combo-" + item.id,
               name: item.name,
               price: item.price
             }));

           default:
             return [];
        }
   };

  return (
    <div className="frontdesk-dashboard">
      {/* HEADER */}
      <header className="frontdesk-header">
        <div>
          <h1>🍕 Casa Pizza Frontdesk</h1>
          <p>Sistema de mostrador premium</p>
        </div>

        <div className="header-right">
          <span className="status-open">● Caja Abierta</span>
          {lastOrderId && (
            <span className="last-order">Última orden #{lastOrderId}</span>
          )}
          <button className="shift-btn" onClick={openShiftModal}>
            📊 Corte de Caja
          </button>
        </div>
      </header>

      {/* BODY */}
      <main className="frontdesk-main">
        {/* PRODUCTOS */}
        <section className="products-panel">
          {categories.map((category) => (
            <div key={category} className="category-block">
              <h2>{category}</h2>

              <div className="product-grid">
                {getProductsByCategory(category).map((product) => (
                  <button
                    key={product.id}
                    className="product-btn"
                    /*onClick={() => addProduct(product)}*/
                    onClick={() => {
                      if (category === "PIZZAS") {
                        setSelectedPizza(product);
                        setSelectedSpecialties([]);
                        setShowPizzaModal(true);
                      } else {
                        addProduct(product);
                      }
                    }}
                  >
                    <span>{product.name}</span>
                    <strong>${product.price}</strong>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* TICKET */}
        <aside className="ticket-panel">
          <h2>🧾 Ticket Actual</h2>

          <div className="customer-box">
            <input
              type="text"
              placeholder="Cliente"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />

            <input
              type="text"
              placeholder="Teléfono"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
            />
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(Number(e.target.value))}
            >
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          <div className="ticket-items">
            {ticket.length === 0 ? (
              <p className="empty-ticket">Sin productos agregados</p>
            ) : (
              ticket.map((item) => (
                <div key={item.id} className="ticket-row">
                  <div>
                    <strong>{item.name}</strong>

                    {item.specialties && item.specialties.length > 0 && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginTop: "4px",
                        }}
                      >
                        {item.specialties.map((sp, i) => (
                          <div key={i}>• {sp}</div>
                        ))}
                      </div>
                    )}

                    <small>
                      ${item.price} x {item.qty}
                    </small>
                  </div>

                  <div className="qty-controls">
                    <button onClick={() => decreaseQty(item.id)}>-</button>
                    <span>{item.qty}</span>
                    <button onClick={() => increaseQty(item.id)}>+</button>
                    <button
                      className="remove-btn"
                      onClick={() => removeItem(item.id)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <textarea
            placeholder="Notas del pedido..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <div className="payment-box">
            <button
              className={
                paymentMethod === "EFECTIVO" ? "pay-btn active" : "pay-btn"
              }
              onClick={() => setPaymentMethod("EFECTIVO")}
            >
              💵 Efectivo
            </button>

            <button
              className={
                paymentMethod === "TARJETA" ? "pay-btn active" : "pay-btn"
              }
              onClick={() => setPaymentMethod("TARJETA")}
            >
              💳 Tarjeta
            </button>

            <button
              className={
                paymentMethod === "TRANSFERENCIA" ? "pay-btn active" : "pay-btn"
              }
              onClick={() => setPaymentMethod("TRANSFERENCIA")}
            >
              📲 Transferencia
            </button>
          </div>

          <div className="totals-box">
            <div>
              <span>Subtotal</span>
              <strong>${subtotal}</strong>
            </div>

            <div className="grand-total">
              <span>Total</span>
              <strong>${total}</strong>
            </div>
          </div>

          <div className="actions-box">
            <button className="clear-btn" onClick={clearOrder}>
              Limpiar
            </button>

            <button
              className="send-btn"
              onClick={submitOrder}
              disabled={sending}
            >
              {sending ? "Enviando..." : "Enviar a Cocina"}
            </button>
          </div>
        </aside>
      </main>

      {showShiftModal && (
        <div className="pizza-modal">
          <div className="pizza-modal-content" style={{ maxWidth: 560, width: "90%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>📊 Corte de Caja</h2>
              <button onClick={() => setShowShiftModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <button
                onClick={() => { setShiftView("summary"); loadShiftSummary(); }}
                style={{ flex: 1, padding: "8px", background: shiftView === "summary" ? "#111" : "#eee", color: shiftView === "summary" ? "#fff" : "#111", border: "none", borderRadius: 6, cursor: "pointer" }}
              >
                Turno actual
              </button>
              <button
                onClick={() => { setShiftView("history"); loadCutHistory(); }}
                style={{ flex: 1, padding: "8px", background: shiftView === "history" ? "#111" : "#eee", color: shiftView === "history" ? "#fff" : "#111", border: "none", borderRadius: 6, cursor: "pointer" }}
              >
                Historial
              </button>
            </div>

            {shiftView === "summary" && (
              <>
                {!shiftSummary ? (
                  <p>Cargando...</p>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                      {[
                        { label: "💵 Efectivo", value: shiftSummary.total_efectivo },
                        { label: "💳 Tarjeta", value: shiftSummary.total_tarjeta },
                        { label: "📲 Transferencia", value: shiftSummary.total_transferencia },
                        { label: "🧾 Total general", value: shiftSummary.grand_total },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ background: "#f5f5f5", borderRadius: 8, padding: "10px 14px" }}>
                          <div style={{ fontSize: 13, color: "#666" }}>{label}</div>
                          <div style={{ fontSize: 20, fontWeight: "bold" }}>${value.toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginBottom: 12, fontSize: 13, color: "#555" }}>
                      {shiftSummary.total_orders} órdenes en el turno
                      {shiftSummary.opened_at && (
                        <span> · desde {new Date(shiftSummary.opened_at).toLocaleString()}</span>
                      )}
                    </div>
                    <div style={{ maxHeight: 180, overflowY: "auto", marginBottom: 16 }}>
                      {shiftSummary.orders.map((o) => (
                        <div key={o.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #eee", fontSize: 13 }}>
                          <span>#{o.id} {o.customer_name}</span>
                          <span style={{ color: "#666" }}>{o.payment_method}</span>
                          <strong>${o.total_amount.toFixed(2)}</strong>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={executeCloseShift}
                      disabled={closingShift || shiftSummary.total_orders === 0}
                      style={{ width: "100%", padding: "12px", background: shiftSummary.total_orders === 0 ? "#ccc" : "#dc2626", color: "#fff", border: "none", borderRadius: 8, fontWeight: "bold", fontSize: 15, cursor: shiftSummary.total_orders === 0 ? "not-allowed" : "pointer" }}
                    >
                      {closingShift ? "Cerrando turno..." : "Ejecutar Corte"}
                    </button>
                  </>
                )}
              </>
            )}

            {shiftView === "history" && (
              <>
                <div style={{ maxHeight: 320, overflowY: "auto", marginBottom: 12 }}>
                  {cutHistory.length === 0 ? (
                    <p style={{ color: "#888", textAlign: "center" }}>Sin cortes anteriores</p>
                  ) : (
                    cutHistory.map((c) => (
                      <div key={c.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                          <strong>Corte #{c.id}</strong>
                          <span style={{ fontSize: 13, color: "#666" }}>{new Date(c.closed_at).toLocaleString()}</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, fontSize: 13 }}>
                          <span>💵 ${c.total_efectivo.toFixed(2)}</span>
                          <span>💳 ${c.total_tarjeta.toFixed(2)}</span>
                          <span>📲 ${c.total_transferencia.toFixed(2)}</span>
                        </div>
                        <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                          <span>{c.total_orders} órdenes · {c.closed_by}</span>
                          <strong>${c.grand_total.toFixed(2)}</strong>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <button
                  onClick={() => setShowShiftModal(false)}
                  style={{ width: "100%", padding: "12px", background: "#111", color: "#fff", border: "none", borderRadius: 8, fontWeight: "bold", fontSize: 15, cursor: "pointer" }}
                >
                  Continuar con la venta
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showPizzaModal && selectedPizza && (
        <div className="pizza-modal">
          <div className="pizza-modal-content">
            <h2>{selectedPizza.name}</h2>

            <p>
              Especialidades {selectedSpecialties.length}/
              {selectedPizza.max_specialties}
            </p>

            {(catalog.specialties || []).map((sp) => (
              <label key={sp.id} style={{ display: "block", margin: "8px 0" }}>
                <input
                  type="checkbox"
                  checked={selectedSpecialties.includes(sp.name)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      if (
                        selectedSpecialties.length <
                        selectedPizza.max_specialties
                      ) {
                        setSelectedSpecialties([
                          ...selectedSpecialties,
                          sp.name,
                        ]);
                      }
                    } else {
                      setSelectedSpecialties(
                        selectedSpecialties.filter((x) => x !== sp.name),
                      );
                    }
                  }}
                />
                {sp.name}
              </label>
            ))}

            <button
              onClick={() => {
                addProduct({
                  ...selectedPizza,
                  qty: 1,
                  specialties: selectedSpecialties,
                });

                setShowPizzaModal(false);
              }}
            >
              Agregar Pizza
            </button>

            <button onClick={() => setShowPizzaModal(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FrontdeskDashboard;